import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Button } from 'primereact/button';
import CBPromptModal from '../../shared/CBPromptModal';
import {
  updateConsolidatedInvoiceDraft as _updateConsolidatedInvoiceDraft,
} from '../../../actions/chainActions';
import {
  VAT_OPTIONS, parseDecimal, toVatOption, isKnownVatOption,
} from './invoiceEditHelpers';

function formatPrice(value) {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return Number(value).toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';
}

// Matches Python's ROUND_HALF_UP (halves away from zero) — Math.round alone
// rounds -0.5 towards +Infinity.
function roundHalfUp2(value) {
  var sign = value < 0 ? -1 : 1;
  return sign * (Math.round((Math.abs(value) + Number.EPSILON) * 100) / 100);
}

function rowTotal(item) {
  var qty = parseDecimal(item.quantity);
  var price = parseDecimal(item.unit_price);
  if (qty === null || price === null) return null;
  return roundHalfUp2(price * qty);
}

function rowIsValid(item) {
  var qty = parseDecimal(item.quantity);
  return (
    item.description && item.description.trim() !== ''
    && qty !== null && qty !== 0
    && parseDecimal(item.unit_price) !== null
    && isKnownVatOption(item.vat_percentage)
  );
}

class ConsolidatedInvoiceEditDialog extends React.Component {
  constructor(props) {
    super(props);
    var keyCounter = 0;
    var groups = [];
    var preview = props.consolidatedInvoicePreview;
    if (preview && preview.vehicles) {
      groups = preview.vehicles.map(function (vehicle) {
        return {
          registration: vehicle.registration,
          make_model: vehicle.make_model || '',
          items: vehicle.items.map(function (item) {
            keyCounter += 1;
            return {
              _key: 'row' + keyCounter,
              id: item.id,
              description: item.description || '',
              item_type: item.item_type || 'other',
              product_code: item.product_code || '',
              quantity: String(item.quantity),
              unit: item.unit || '',
              unit_price: String(item.unit_price),
              vat_percentage: toVatOption(item.vat_percentage),
              vehicle_registration: item.vehicle_registration || '',
              vehicle_make_model: item.vehicle_make_model || '',
              workshop_name: item.workshop_name || '',
              service_date: item.service_date || null,
            };
          }),
        };
      });
    }
    this.state = {
      groups,
      keyCounter,
      expectedUpdatedAt: preview && preview.header ? preview.header.updated_at : null,
      dirty: false,
      submitted: false,
      errorMessage: null,
      discardConfirmOpen: false,
      newVehicleReg: '',
      newVehicleMakeModel: '',
    };
  }

  componentDidUpdate(prevProps) {
    var prevStatus = prevProps.updateConsolidatedInvoiceStatus;
    var status = this.props.updateConsolidatedInvoiceStatus;
    if (this.state.submitted && prevStatus && prevStatus.loading && status && !status.loading) {
      if (!status.error) {
        this.props.onSaved();
      } else {
        var t = this.props.t;
        var detail = status.error && status.error.detail;
        this.setState({
          submitted: false,
          errorMessage: detail || t('saveFailed'),
        });
      }
    }
  }

  updateItem = function (groupIndex, itemKey, field, value) {
    var groups = this.state.groups.map(function (group, gi) {
      if (gi !== groupIndex) return group;
      return {
        ...group,
        items: group.items.map(function (item) {
          if (item._key !== itemKey) return item;
          return { ...item, [field]: value };
        }),
      };
    });
    this.setState({ groups, dirty: true, errorMessage: null });
  };

  removeItem = function (groupIndex, itemKey) {
    var groups = this.state.groups
      .map(function (group, gi) {
        if (gi !== groupIndex) return group;
        return {
          ...group,
          items: group.items.filter(function (item) { return item._key !== itemKey; }),
        };
      })
      .filter(function (group) { return group.items.length > 0; });
    this.setState({ groups, dirty: true, errorMessage: null });
  };

  addRow = function (groupIndex) {
    var keyCounter = this.state.keyCounter + 1;
    var groups = this.state.groups.map(function (group, gi) {
      if (gi !== groupIndex) return group;
      // Inherit registration from an existing row in the group (its true
      // stored value — the group label may be the TUNTEMATON placeholder).
      var template = group.items.length > 0 ? group.items[0] : null;
      return {
        ...group,
        items: group.items.concat([{
          _key: 'row' + keyCounter,
          id: null,
          description: '',
          item_type: 'work',
          product_code: '',
          quantity: '1',
          unit: '',
          unit_price: '',
          vat_percentage: '25.5',
          vehicle_registration: template ? template.vehicle_registration : group.registration,
          vehicle_make_model: template ? template.vehicle_make_model : group.make_model,
          workshop_name: template ? template.workshop_name : '',
          service_date: null,
        }]),
      };
    });
    this.setState({ groups, keyCounter, dirty: true });
  };

  addVehicleGroup = function () {
    var registration = this.state.newVehicleReg.trim().toUpperCase();
    if (!registration) return;
    var makeModel = this.state.newVehicleMakeModel.trim();
    var keyCounter = this.state.keyCounter + 1;
    var groups = this.state.groups.concat([{
      registration,
      make_model: makeModel,
      items: [{
        _key: 'row' + keyCounter,
        id: null,
        description: '',
        item_type: 'work',
        product_code: '',
        quantity: '1',
        unit: '',
        unit_price: '',
        vat_percentage: '25.5',
        vehicle_registration: registration,
        vehicle_make_model: makeModel,
        workshop_name: '',
        service_date: null,
      }],
    }]);
    this.setState({
      groups,
      keyCounter,
      dirty: true,
      newVehicleReg: '',
      newVehicleMakeModel: '',
    });
  };

  handleClose = function () {
    if (this.state.dirty) {
      this.setState({ discardConfirmOpen: true });
    } else {
      this.props.onClose();
    }
  };

  allRows = function () {
    var rows = [];
    this.state.groups.forEach(function (group) {
      group.items.forEach(function (item) { rows.push(item); });
    });
    return rows;
  };

  isValid = function () {
    var rows = this.allRows();
    if (rows.length === 0) return false;
    return rows.every(rowIsValid);
  };

  computeTotals = function () {
    var net = 0;
    var vat = 0;
    this.allRows().forEach(function (item) {
      var total = rowTotal(item);
      if (total === null) return;
      net += total;
      var vatPct = parseDecimal(item.vat_percentage) || 0;
      vat += roundHalfUp2(total * vatPct / 100);
    });
    return { net: roundHalfUp2(net), vat: roundHalfUp2(vat), gross: roundHalfUp2(net + vat) };
  };

  handleSave = function () {
    var t = this.props.t;
    if (this.allRows().length === 0) {
      this.setState({ errorMessage: t('minOneRowRequired') });
      return;
    }
    if (!this.isValid()) {
      this.setState({ errorMessage: t('fillRequiredFields') });
      return;
    }
    var items = [];
    var sortOrder = 0;
    this.state.groups.forEach(function (group) {
      group.items.forEach(function (item) {
        items.push({
          id: item.id,
          description: item.description.trim(),
          item_type: item.item_type,
          product_code: item.product_code,
          quantity: String(parseDecimal(item.quantity)),
          unit: item.unit,
          unit_price: String(parseDecimal(item.unit_price)),
          vat_percentage: item.vat_percentage,
          vehicle_registration: item.vehicle_registration,
          vehicle_make_model: item.vehicle_make_model,
          workshop_name: item.workshop_name,
          service_date: item.service_date || null,
          sort_order: sortOrder,
        });
        sortOrder += 1;
      });
    });
    this.setState({ submitted: true, errorMessage: null });
    this.props.updateConsolidatedInvoiceDraft(this.props.invoiceId, {
      expected_updated_at: this.state.expectedUpdatedAt,
      items,
    });
  };

  renderItemRow = function (group, groupIndex, item) {
    var t = this.props.t;
    var updateItem = this.updateItem.bind(this);
    var removeItem = this.removeItem.bind(this);
    var total = rowTotal(item);
    var invalid = !rowIsValid(item);
    // A stored rate the dropdown does not offer (or none at all) is shown as
    // its own flagged option rather than masquerading as the first one.
    var vatUnknown = !isKnownVatOption(item.vat_percentage);

    return (
      <tr key={item._key}>
        <td style={styles.eTd}>
          <input
            style={{
              ...styles.input,
              minWidth: 180,
              ...(item.description.trim() === '' ? styles.inputInvalid : {}),
            }}
            value={item.description}
            maxLength={500}
            onChange={function (e) { updateItem(groupIndex, item._key, 'description', e.target.value); }}
          />
        </td>
        <td style={styles.eTd}>
          <select
            className="dark-select"
            style={styles.select}
            value={item.item_type}
            onChange={function (e) { updateItem(groupIndex, item._key, 'item_type', e.target.value); }}
          >
            <option value="work">{t('workItems')}</option>
            <option value="part">{t('partItems')}</option>
            <option value="work_summary">{t('itemTypeWorkSummary')}</option>
            <option value="parts_summary">{t('itemTypePartsSummary')}</option>
            <option value="other">{t('itemTypeOther')}</option>
          </select>
        </td>
        <td style={styles.eTd}>
          <input
            style={{
              ...styles.input,
              width: 70,
              textAlign: 'right',
              ...(parseDecimal(item.quantity) === null || parseDecimal(item.quantity) === 0 ? styles.inputInvalid : {}),
            }}
            value={item.quantity}
            inputMode="decimal"
            onChange={function (e) { updateItem(groupIndex, item._key, 'quantity', e.target.value); }}
          />
        </td>
        <td style={styles.eTd}>
          <input
            style={{ ...styles.input, width: 60 }}
            value={item.unit}
            maxLength={20}
            onChange={function (e) { updateItem(groupIndex, item._key, 'unit', e.target.value); }}
          />
        </td>
        <td style={styles.eTd}>
          <input
            style={{
              ...styles.input,
              width: 90,
              textAlign: 'right',
              ...(parseDecimal(item.unit_price) === null ? styles.inputInvalid : {}),
            }}
            value={item.unit_price}
            inputMode="decimal"
            onChange={function (e) { updateItem(groupIndex, item._key, 'unit_price', e.target.value); }}
          />
        </td>
        <td style={styles.eTd}>
          <select
            className="dark-select"
            style={{
              ...styles.select,
              width: 80,
              ...(vatUnknown ? styles.inputInvalid : {}),
            }}
            value={item.vat_percentage}
            onChange={function (e) { updateItem(groupIndex, item._key, 'vat_percentage', e.target.value); }}
          >
            {vatUnknown && (
              <option value={item.vat_percentage}>
                {item.vat_percentage === '' ? '-' : item.vat_percentage.replace('.', ',') + ' %'}
              </option>
            )}
            {VAT_OPTIONS.map(function (rate) {
              return <option key={rate} value={rate}>{rate.replace('.', ',')} %</option>;
            })}
          </select>
        </td>
        <td style={styles.eTd}>
          <input
            className="white-placeholder-input"
            style={{ ...styles.input, width: 90 }}
            value={item.product_code}
            maxLength={100}
            placeholder={t('productCode')}
            onChange={function (e) { updateItem(groupIndex, item._key, 'product_code', e.target.value); }}
          />
        </td>
        <td style={styles.eTd}>
          <input
            type="date"
            style={{ ...styles.input, width: 130 }}
            value={item.service_date || ''}
            onChange={function (e) { updateItem(groupIndex, item._key, 'service_date', e.target.value || null); }}
          />
        </td>
        <td style={{ ...styles.eTd, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color: invalid ? 'rgba(255,255,255,0.3)' : '#fff' }}>
          {total !== null ? formatPrice(total) : '-'}
        </td>
        <td style={{ ...styles.eTd, textAlign: 'right' }}>
          <Button
            icon="pi pi-trash"
            text
            rounded
            onClick={function () { removeItem(groupIndex, item._key); }}
            style={{ color: '#EF9A9A', width: 30, height: 30 }}
          />
        </td>
      </tr>
    );
  };

  render() {
    var t = this.props.t;
    var status = this.props.updateConsolidatedInvoiceStatus;
    var saving = status && status.loading;
    var totals = this.computeTotals.bind(this)();
    var renderItemRow = this.renderItemRow.bind(this);
    var addRow = this.addRow.bind(this);
    var handleClose = this.handleClose.bind(this);
    var canSave = this.state.dirty && this.isValid.bind(this)() && !saving;

    return (
      <div style={styles.overlay} onClick={handleClose}>
        <div style={styles.dialog} onClick={function (e) { e.stopPropagation(); }}>
          <div style={styles.dialogHeader}>
            <h3 style={styles.dialogTitle}>{t('editInvoice')}</h3>
            <Button
              icon="pi pi-times"
              text
              rounded
              onClick={handleClose}
              style={{ color: '#fff', width: 32, height: 32 }}
            />
          </div>

          <div style={styles.dialogContent}>
            {this.state.groups.map(function (group, groupIndex) {
              var groupTotal = 0;
              group.items.forEach(function (item) {
                var total = rowTotal(item);
                if (total !== null) groupTotal += total;
              });
              return (
                <div key={group.registration + '_' + groupIndex} style={styles.vehicleCard}>
                  <div style={styles.vehicleHeader}>
                    <span style={styles.vehicleReg}>{group.registration}</span>
                    {group.make_model && (
                      <span style={styles.vehicleMakeModel}>{group.make_model}</span>
                    )}
                    <span style={styles.vehicleSubtotal}>{formatPrice(roundHalfUp2(groupTotal))}</span>
                  </div>
                  <div style={styles.vehicleTableWrapper}>
                    <table style={styles.editTable}>
                      <thead>
                        <tr>
                          <th style={styles.eTh}>{t('description')}</th>
                          <th style={styles.eTh}>{t('type')}</th>
                          <th style={{ ...styles.eTh, textAlign: 'right' }}>{t('quantity')}</th>
                          <th style={styles.eTh}>{t('unit')}</th>
                          <th style={{ ...styles.eTh, textAlign: 'right' }}>{t('unitPrice')} {t('vatExclusive')}</th>
                          <th style={styles.eTh}>{t('vat')}</th>
                          <th style={styles.eTh}>{t('productCode')}</th>
                          <th style={styles.eTh}>{t('serviceDate')}</th>
                          <th style={{ ...styles.eTh, textAlign: 'right' }}>{t('rowTotal')}</th>
                          <th style={styles.eTh} />
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map(function (item) {
                          return renderItemRow(group, groupIndex, item);
                        })}
                      </tbody>
                    </table>
                  </div>
                  <Button
                    label={t('addRow')}
                    icon="pi pi-plus"
                    text
                    size="small"
                    onClick={function () { addRow(groupIndex); }}
                    style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4 }}
                  />
                </div>
              );
            })}

            {this.state.groups.length === 0 && (
              <p style={styles.noDataText}>{t('minOneRowRequired')}</p>
            )}

            <div style={styles.addVehicleBar}>
              <input
                className="white-placeholder-input"
                style={{ ...styles.input, width: 130 }}
                value={this.state.newVehicleReg}
                maxLength={20}
                placeholder={t('newVehicleRegistration')}
                onChange={function (e) { this.setState({ newVehicleReg: e.target.value }); }.bind(this)}
              />
              <input
                className="white-placeholder-input"
                style={{ ...styles.input, width: 180 }}
                value={this.state.newVehicleMakeModel}
                maxLength={255}
                placeholder={t('makeModel')}
                onChange={function (e) { this.setState({ newVehicleMakeModel: e.target.value }); }.bind(this)}
              />
              <Button
                label={t('addVehicle')}
                icon="pi pi-plus"
                size="small"
                disabled={this.state.newVehicleReg.trim() === ''}
                onClick={this.addVehicleGroup.bind(this)}
                style={styles.actionButton}
              />
            </div>
          </div>

          {/* Sticky footer: live totals + save */}
          <div style={styles.footer}>
            <div style={styles.footerTotals}>
              <span style={styles.footerTotalItem}>
                {t('total')} {t('vatExclusive')}: <strong>{formatPrice(totals.net)}</strong>
              </span>
              <span style={styles.footerTotalItem}>
                {t('vat')}: <strong>{formatPrice(totals.vat)}</strong>
              </span>
              <span style={{ ...styles.footerTotalItem, fontSize: 16 }}>
                {t('grandTotal')}: <strong>{formatPrice(totals.gross)}</strong>
              </span>
              <span style={styles.footerNote}>{t('livePreviewTotalsNote')}</span>
            </div>
            {this.state.errorMessage && (
              <div style={styles.errorBanner}>{this.state.errorMessage}</div>
            )}
            <div style={styles.footerButtons}>
              <Button
                label={t('cancel')}
                onClick={handleClose}
                text
                style={{ color: 'rgba(255,255,255,0.8)' }}
              />
              <Button
                label={saving ? t('saving') : t('saveChanges')}
                icon="pi pi-check"
                disabled={!canSave}
                onClick={this.handleSave.bind(this)}
                style={{ ...styles.actionButton, backgroundColor: 'rgba(129, 199, 132, 0.2)', borderColor: 'rgba(129, 199, 132, 0.4)' }}
              />
            </div>
          </div>

          <CBPromptModal
            modalIsOpen={this.state.discardConfirmOpen}
            closeModal={function () { this.setState({ discardConfirmOpen: false }); }.bind(this)}
            performAction={function () {
              this.setState({ discardConfirmOpen: false });
              this.props.onClose();
            }.bind(this)}
            title={t('editInvoice')}
            text={t('confirmDiscardChanges')}
            buttonText={t('discard')}
          />
        </div>
      </div>
    );
  }
}

var styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    // Above ConsolidatedInvoiceDetail (1000), below CBPromptModal (1100)
    zIndex: 1050,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    paddingTop: 40,
    overflowY: 'auto',
  },
  dialog: {
    backgroundColor: '#0077b0',
    borderRadius: 12,
    border: '1px solid rgba(255, 255, 255, 0.15)',
    width: '95%',
    maxWidth: 1100,
    minWidth: 0,
    boxSizing: 'border-box',
    maxHeight: 'calc(100vh - 80px)',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 40,
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: '#0077b0',
    flexShrink: 0,
  },
  dialogTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 600,
    margin: 0,
  },
  dialogContent: {
    padding: '20px 24px',
    overflowY: 'auto',
    flex: 1,
    minHeight: 0,
  },
  vehicleCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: 16,
    marginBottom: 12,
  },
  vehicleHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  vehicleReg: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
  },
  vehicleMakeModel: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 13,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  vehicleSubtotal: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 600,
  },
  vehicleTableWrapper: {
    width: '100%',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  editTable: {
    width: '100%',
    minWidth: 980,
    borderCollapse: 'collapse',
  },
  eTh: {
    padding: '6px 6px',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    textAlign: 'left',
  },
  eTd: {
    padding: '4px 6px',
    color: '#fff',
    fontSize: 13,
    borderBottom: '1px solid rgba(255, 255, 255, 0.04)',
    verticalAlign: 'middle',
  },
  input: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 6,
    color: '#fff',
    fontSize: 13,
    padding: '6px 8px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
  },
  inputInvalid: {
    borderColor: 'rgba(229, 115, 115, 0.7)',
  },
  select: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: 6,
    color: '#fff',
    fontSize: 13,
    padding: '6px 8px',
    outline: 'none',
  },
  addVehicleBar: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    marginTop: 8,
    padding: '12px 0',
    borderTop: '1px dashed rgba(255, 255, 255, 0.15)',
  },
  actionButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.25)',
    color: '#fff',
    fontWeight: 600,
  },
  footer: {
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    backgroundColor: '#006699',
    borderRadius: '0 0 12px 12px',
    padding: '12px 24px',
    flexShrink: 0,
  },
  footerTotals: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 20,
    flexWrap: 'wrap',
    marginBottom: 8,
  },
  footerTotalItem: {
    color: '#fff',
    fontSize: 13,
  },
  footerNote: {
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 11,
  },
  footerButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  errorBanner: {
    backgroundColor: 'rgba(229, 115, 115, 0.15)',
    border: '1px solid rgba(229, 115, 115, 0.4)',
    borderRadius: 8,
    padding: '8px 12px',
    marginBottom: 8,
    color: '#EF9A9A',
    fontSize: 13,
  },
  noDataText: {
    color: 'rgba(255, 255, 255, 0.4)',
    fontSize: 14,
    textAlign: 'center',
    padding: 24,
  },
};

function mapStateToProps(state) {
  return {
    consolidatedInvoicePreview: state.chain.consolidatedInvoicePreview,
    updateConsolidatedInvoiceStatus: state.chain.updateConsolidatedInvoiceStatus,
  };
}

export default connect(
  mapStateToProps,
  {
    updateConsolidatedInvoiceDraft: _updateConsolidatedInvoiceDraft,
  },
)(translate('ChainInvoicingView')(ConsolidatedInvoiceEditDialog));
